<script lang="ts">
import { defineComponent, PropType, ref, computed } from 'vue'
import InputProfileName from '@/components/lead/InputProfileName.vue'
import InputFormMailAddress from '@/components/lead/InputFormMailAddress.vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import ButtonDefault from '@/components/button/ButtonDefault.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
// import OAuthOptions from '@/components/sns/OAuthOptions.vue'
import validationOperator from '@/models/validationOperator'
import { sendGtmEvent } from '@/utilities/googleTagManager'
import {
  staticPagesTermsPath,
  staticPagesPrivacyPath,
} from '@/generated/railsRoutes'

export default defineComponent({
  name: 'EntranceStep',

  components: {
    ValidationObserver,
    InputProfileName,
    InputFormMailAddress,
    ButtonDefault,
    ButtonSubmit,
    // OAuthOptions,
  },

  props: {
    namespace: {
      type: String,
      required: true,
    },

    firstName: {
      type: Object as PropType<InputText>,
      required: true,
    },

    lastName: {
      type: Object as PropType<InputText>,
      required: true,
    },

    firstNameKana: {
      type: Object as PropType<InputText>,
      required: true,
    },

    lastNameKana: {
      type: Object as PropType<InputText>,
      required: true,
    },

    mailAddress: {
      type: Object as PropType<InputText>,
      required: true,
    },

    /**
     * ソーシャルボタンの Callback かどうか
     *
     * @description
     *  登録導線において、ソーシャルボタンをクリックして OAuth 認証を通した後、
     *  Callback として登録画面の最初に帰って来るが、その際サーバから use_omniauth = true で返却される。
     *  その場合（OAuth 認証後の Callbackの場合）は、ソーシャルボタンを表示しない・メルアド欄が readonly になるといった制御が入る。
     */
    isOAuthCallback: {
      type: Boolean,
      required: true,
    },
  },

  emits: [
    'update:firstName',
    'update:lastName',
    'update:firstNameKana',
    'update:lastNameKana',
    'update:mailAddress',
    'click:next'
  ],

  setup(props, { emit }) {
    // constants
    const GA_EVENT_NAME = 'sign_up_next' as const

    const { validationObserver, validate } = validationOperator()

    const isFreeMainDomain = ref(false)
    const isShowModal = ref(false)
    const isConfirmationForFreeMail = ref(false)

    const userNameProfile = computed(() => {
      return {
        firstName: props.firstName,
        lastName: props.lastName,
        firstNameKana: props.firstNameKana,
        lastNameKana: props.lastNameKana,
      } as UserLeadInfo
    })

    const submitButtonCaptioin = computed(() => {
      return `<a class="a" href="${staticPagesTermsPath()}" target="_blank">利用規約</a>と<a class="a" href="${staticPagesPrivacyPath()}" target="_blank">個人情報の取り扱い</a>について同意のうえ`
    })

    const handleNextStep = () => {
      if (isFreeMainDomain.value) {
        sendGtmEvent(GA_EVENT_NAME, 'personal_email_entrance')
        isShowModal.value = true
      } else {
        sendGtmEvent(GA_EVENT_NAME, 'work_email_entrance')
        next()
      }
    }

    const onClickNextInModal = () => {
      sendGtmEvent(GA_EVENT_NAME, 'personal_email_confirmation')
      next()
    }

    const next = () => {
      isShowModal.value = false
      isConfirmationForFreeMail.value = false
      emit('click:next', isFreeMainDomain.value)
    }

    return {
      // data
      validationObserver,
      isFreeMainDomain,
      isShowModal,
      isConfirmationForFreeMail,

      // computed
      userNameProfile,
      submitButtonCaptioin,

      // methods
      validate,
      handleNextStep,
      next,
      onClickNextInModal,
    }
  },
})
</script>

<template>
  <section class="f-section__signUpEntrance">
    <div class="f-section__signUpEntrance--body">
      <ValidationObserver
        ref="validationObserver"
        :namespace="namespace"
        @valid="handleNextStep">
        <div class="o-heading__page">
          <h1 class="o-heading__page--item">
            無料会員登録（全8ステップ・約1分）
          </h1>
        <!-- /.o-heading__page --></div>

        <InputProfileName
          :user-lead-info="userNameProfile"
          :namespace="namespace"
          @update:last-name="$emit('update:lastName', $event)"
          @update:first-name="$emit('update:firstName', $event)"
          @update:last-name-kana="$emit('update:lastNameKana', $event)"
          @update:first-name-kana="$emit('update:firstNameKana', $event)" />

        <InputFormMailAddress
          :model-value="mailAddress.value"
          :namespace="namespace"
          :name="mailAddress.name"
          :validation-scheme="mailAddress.validationScheme"
          :readonly="isOAuthCallback"
          header="勤務先メールアドレス"
          @update:model-value="$emit('update:mailAddress', $event)"
          @check:domain="isFreeMainDomain = $event" />

        <slot />

        <div class="o-note__general u-text__x-small">
          <ul class="o-note__general--items">
            <li class="o-note__general--item">
              <strong class="strong">勤務先メールアドレスでのご登録をお願いいたします。</strong>
            </li>
            <li class="o-note__general--item">
              ログインIDとなります。
            </li>
          </ul>
        <!-- /.o-note__general --></div>

        <ButtonSubmit
          :is-link="false"
          :has-shadow="false"
          prevent
          stop
          :caption="submitButtonCaptioin"
          :width="384"
          @click:submit="validate">
          <div class="o-label__submit">
            <span class="o-label__submit--item is-primary">
              無料
            </span>
            <span class="o-label__submit--item is-secondary">
              登録する
            </span>
          <!-- /.o-label__submit --></div>
        </ButtonSubmit>

        <!-- 一旦、ソーシャルサービス登録は非表示 -->
        <!-- <OAuthOptions
          v-if="!isOAuthCallback"
          :is-sign-in="false" /> -->

        <ModalDefault
          v-model:is-show="isShowModal"
          title="注意"
          size="large"
          is-blur>
          <template #description>
            <p class="p">
              BizHintは<strong class="strong">勤務先メールアドレスでのご登録を推奨</strong>しております。<br>
              フリーメールアドレスでご登録の場合、<em class="em u-text__underline">一部機能に制限が設けられております</em>ので、あらかじめご了承ください。
            </p>

            <p class="p u-text__center">
              <label class="label">
                <input
                  v-model="isConfirmationForFreeMail"
                  class="input"
                  type="checkbox"
                  :value="true">
                確認しました
                <span class="span u-text__required">
                  必須
                </span>
              </label>
            </p>
          </template>

          <template #action>
            <div
              class="l-cols is-col2"
              data-breakpoint="sp">
              <div class="l-cols--col">
                <div class="o-action__reset">
                  <ButtonDefault
                    emit-name="click:cancel"
                    :is-link="false"
                    is-font-small
                    is-large
                    prevent
                    color="orange"
                    @click:cancel="isShowModal = false">
                    編集に戻る
                  </ButtonDefault>
                <!-- /.o-action__reset --></div>
              <!-- /.l-cols col --></div>

              <div class="l-cols--col">
                <ButtonSubmit
                  emit-name="click:next"
                  :is-link="false"
                  is-font-small
                  is-large
                  :disabled="!isConfirmationForFreeMail"
                  prevent
                  @click:next="onClickNextInModal">
                  次へ
                </ButtonSubmit>
              <!-- /.l-cols col --></div>
            <!-- /.l-cols --></div>
          </template>
        </ModalDefault>
      </ValidationObserver>
    <!-- /.f-section__signUpEntrance body --></div>
  <!-- /.f-section__signUpEntrance --></section>
</template>

<style lang="sass" scoped>
.f-section__signUpEntrance
  $root: &

  & &--body
    padding: 2.5em 3em

  +breakpoint
    #{$root}--body
      padding:
        left: 1.5em
        right: 1.5em

.o-heading__page
  & &--item
    line-height: 1.4
    font:
      size: $font-huge
      weight: bold
    padding-bottom: 0.2em
    border-bottom: 1px solid $border-x-light-gray

.o-label__submit
  display: flex
  align-items: center
  justify-content: center

  & &--item
    display: block
    font-weight: bold

    &.is-primary
      line-height: 1
      font-size: $font-small
      margin-right: 0.6em
      padding: 0.25em 0.3em 0.15em
      border: 1px solid $border-white
      border-radius: 4px

    &.is-secondary
      font-size: $font-large
</style>
